import type { View, ViewContext } from '../router';
import { SKILLS, type AudioAsset, type Skill, type SoundType, type Species } from '../../types';
import { el, ensureStyle } from '../dom';
import { findPackFor, getSpecies, speciesList } from '../../core/region';
import { isIgnored, toggleIgnored } from '../../core/ignored';
import { hasTraits } from '../../core/traits';
import {
  dueLabel,
  speciesStanding,
  standingWord,
  type SkillStanding,
} from '../../core/progress';
import { waveformPlayer } from '../components/waveform-player';
import { photoCredit, speciesPhoto } from '../components/species-photo';
import { masteryDot, SKILL_COLOR, SKILL_LABEL } from '../components/mastery-dots';
import { openSpeciesPicker } from '../components/species-picker';
import { icon } from '../components/icons';
import { traitPills } from '../components/trait-pills';
import { sourceLabel } from '../../core/attributions';
import { t, tParts } from '../../strings';

ensureStyle(
  'species-css',
  `
  .species { padding:18px var(--pad); display:flex; flex-direction:column; gap:16px; }
  .species .back { margin-left:-6px; }
  /* Back on the left, the walk along the shelf on the right. The two arrows sit
     together because they are one control used twice; setting a bird aside is
     held apart from them by the gap, being the only button here that changes
     anything. */
  .spbar { display:flex; align-items:center; gap:2px; }
  .spbar .spread { flex:1; }
  .spbar .aside { margin-right:6px; color:var(--ink-soft); }
  /* "Compare to" — a word, not a bare glyph. It is the one control here that
     opens something rather than toggling something, and a split-frame icon is
     not yet a thing anyone has been taught to read. */
  .spbar .compare, .spbar .drill { gap:5px; padding:6px 9px; font-size:12.5px;
    color:var(--ink-soft); }
  .spbar .compare:hover, .spbar .compare:focus-visible,
  .spbar .drill:hover, .spbar .drill:focus-visible { color:var(--ink); }
  .spbar .aside.on { color:var(--accent); }
  .spbar .step:disabled { opacity:.3; }
  /* The name of the bird one step along, on hover and for a screen reader; the
     arrows themselves stay wordless. */
  .spbar .step { color:var(--ink-soft); }
  .species h1 { font-size:32px; margin:0; }
  .species .sci { font-family:var(--font-display); font-style:italic; color:var(--ink-soft);
    font-size:16px; }
  /* What the binomial means, as an aside to it. Upright, so the two Latin words
     keep the italic to themselves, and NOWRAP as a unit: it either sits on the
     line after the name or drops whole to the next one, but it never breaks
     across lines mid-gloss. */
  .species .sci .gloss { font-style:normal; font-size:13.5px; white-space:nowrap;
    opacity:.85; }
  .species .taxon .fam { color:var(--ink); }
  .species .gallery { display:flex; flex-direction:column; gap:9px; }
  /* The hero gets the hand-cut edge; the strip below alternates the other way.
     Shaped by the photograph, like the quiz's — see --photo-ratio in
     species-photo.ts. Swapping to a portrait shot from the strip changes the
     frame's height, which is the honest answer to "show me that one". */
  .species .gallery > .spphoto { width:100%; aspect-ratio:var(--photo-ratio, 4/3);
    border-radius:var(--radius-hand); border:1.5px solid var(--hairline); }
  .species .thumbs { display:flex; flex-wrap:wrap; gap:8px; }
  .species .thumbs button { padding:0; border:2px solid transparent;
    border-radius:11px; background:none; line-height:0; }
  .species .thumbs button.on { border-color:var(--accent); }
  .species .thumbs .spphoto { width:56px; height:56px; border-radius:8px; }
  .species .group { display:flex; flex-direction:column; gap:12px; }
  .species .recording { display:flex; flex-direction:column; gap:6px; }
  .species .empty { color:var(--ink-soft); font-style:italic; }
  /* How you're doing on this one bird — the Library's three dots, spelled out.
     LAST on the page and as small as it can legibly be: you come to a bird's
     page to hear it and look at it, and the three schedules are a footnote to
     that. It used to lead the page in two lines per skill plus a paragraph of
     explanation, which put the photograph below the fold on a phone to say
     "never asked" three times. One line per skill now, and the paragraph is the
     section's tooltip. */
  .species .standing { display:flex; flex-direction:column; gap:7px;
    padding:11px 13px 12px; }
  .species .standing > .label { margin-bottom:1px; }
  .species .standing .srow { display:flex; align-items:center; gap:9px; }
  .species .standing .mdot { flex:none; }
  /* Fixed, so the three meters start on one line however long the skill's name
     is — the column edge is what makes three rows read as one block. */
  .species .standing .who { flex:none; width:76px; font-size:8.4px; }
  .species .standing .meter { flex:1; min-width:0; height:5px; }
  .species .standing .pct { flex:none; width:32px; text-align:right;
    font-family:var(--font-display); font-variation-settings:var(--fvs-display);
    font-size:13.5px; line-height:1; }
  .species .standing .when { flex:none; font-size:10.5px; white-space:nowrap; }
  /* A skill that has never asked has no number to show, so the row says so in
     words and steps back rather than drawing an empty bar at full strength. */
  .species .standing .srow.unmet .meter { opacity:0.5; }
`,
);

/** Fixed display order; a bird's song is what you want to hear first. */
const SOUND_ORDER: SoundType[] = ['song', 'call', 'alarm'];

export const speciesDetailView: View = (root: HTMLElement, ctx: ViewContext) => {
  const sp = getSpecies(ctx.params.code);
  const wrap = el('div.species', {});
  wrap.append(
    el(
      'div.spbar',
      {},
      el(
        'button.back.btn.btn-plain',
        // Back to wherever this page was opened from — the Library with its filter
        // and scroll intact, or the list of misses on a finished round — and to
        // the Library when the page was opened cold from a link.
        { onclick: () => ctx.back('/library'), 'aria-label': t('common.back') },
        icon('arrow-left', { size: 22 }),
      ),
      el('span.spread', {}),
      ...(sp
        ? [drillButton(sp, ctx), compareButton(sp, ctx), asideButton(sp), ...stepButtons(sp, ctx)]
        : []),
    ),
  );

  if (!sp) {
    // Reachable in one more way than it used to be: a link to a bird from a pack
    // that isn't currently selected. Naming that pack turns a dead end into an
    // instruction, since the fix is one control away on the Home screen. The
    // lookup reads other manifests, so it lands after the message rather than
    // holding the screen blank while it does.
    const note = el('div.meta', {}, t('species.detail.missing.checking'));
    wrap.append(el('h1', {}, t('species.detail.missing.title')), note);
    root.append(wrap);
    void findPackFor(ctx.params.code).then(
      (pack) => {
        note.textContent = pack
          ? t('species.detail.missing.inPack', { pack: pack.label })
          : t('species.detail.missing.nowhere');
      },
      () => {
        note.textContent = t('species.detail.missing.nowhere');
      },
    );
    return;
  }

  wrap.append(
    el('div', {}, el('h1', {}, sp.common), sciLine(sp)),
    el(
      'div.taxon.label',
      {},
      ...(sp.order && sp.family
        ? tParts('species.detail.taxon', {
            order: sp.order,
            family: el('span.fam', {}, sp.family),
          })
        : [sp.order ?? '', sp.family && el('span.fam', {}, sp.family)]),
    ),
    gallery(sp),
    ...traitRow(sp, ctx),
    ...soundGroups(sp),
    standingBlock(sp),
  );

  root.append(wrap);
  return walkTheShelf(wrap, sp, ctx);
};

// ---- Walking the list ------------------------------------------------------
//
// The Library is a list of a hundred and sixty birds and this is the page for
// one of them, so "the next one" was: back, find your place again, tap. Three
// actions to do the thing a field guide does by turning a page — which made
// this a screen you looked one bird up on rather than one you read.
//
// Three ways in, one walk: the arrows, a horizontal swipe, and the left/right
// keys. All of them go through ctx.replace rather than ctx.navigate, which is
// what keeps eleven birds of browsing from becoming eleven presses of back.

/** How far a touch has to travel across before it counts as a page turn. */
const SWIPE_PX = 55;

/**
 * How much more horizontal than vertical. A swipe down the page that drifts
 * sideways is a scroll, and turning the page under a scrolling thumb is the
 * most annoying thing a gesture like this can do.
 */
const SWIPE_BIAS = 1.6;

/** The species either side of this one, in the order the Library lists them. */
function neighbours(sp: Species): { prev?: Species; next?: Species } {
  const list = speciesList();
  const at = list.findIndex((s) => s.code === sp.code);
  if (at < 0) return {};
  return { prev: list[at - 1], next: list[at + 1] };
}

/**
 * The two arrows. Disabled at the ends of the list rather than wrapping around:
 * the list is taxonomic, so its ends are a goose and a finch, and arriving at
 * one from the other reads as a bug rather than as a loop.
 */
function stepButtons(sp: Species, ctx: ViewContext): HTMLElement[] {
  const { prev, next } = neighbours(sp);
  const make = (to: Species | undefined, dir: 'prev' | 'next') =>
    el(
      'button.step.btn.btn-plain',
      {
        disabled: !to,
        'aria-label': to
          ? t(dir === 'prev' ? 'species.detail.step.prev' : 'species.detail.step.next', {
              name: to.common,
            })
          : t(dir === 'prev' ? 'species.detail.step.noPrev' : 'species.detail.step.noNext'),
        title: to?.common ?? '',
        onclick: () => to && ctx.replace(`/species/${to.code}`),
      },
      icon(dir === 'prev' ? 'arrow-left' : 'arrow-right', { size: 20 }),
    );
  return [make(prev, 'prev'), make(next, 'next')];
}

/**
 * "Compare to" — the way into the side-by-side view from a single bird.
 *
 * The dropdown is not a list of the region in taxonomic order: it leads with
 * the birds this one is actually mistaken for, then its family, then its order
 * (see core/compare.ts), because the comparison a learner wants is nearly
 * always with something they have already confused this one with. It is a
 * NAVIGATION rather than the quizzes' overlay — the species page holds no
 * state that going back would lose, and a comparison reached from here is
 * worth having a URL.
 */
function compareButton(sp: Species, ctx: ViewContext): HTMLElement {
  const btn = el(
    'button.compare.btn.btn-plain',
    {
      type: 'button',
      title: t('species.detail.compare.title', { name: sp.common }),
    },
    icon('compare', { size: 17 }),
    t('species.detail.compare.label'),
  ) as HTMLButtonElement;
  btn.addEventListener('click', () =>
    openSpeciesPicker(btn, {
      anchor: sp,
      exclude: [sp.code],
      onPick: (code) => ctx.navigate(`/compare/${sp.code}/${code}`),
    }),
  );
  return btn;
}

/**
 * "Drill" — this one bird, asked every way the app knows how.
 *
 * The page you are on when you want this is the page of a bird you have just
 * got wrong, or one you are about to go out and look for, and until now the
 * only way to study it was to hope the scheduler brought it round. It is a
 * focused Field Test of one species (see core/quiz/focus.ts), which deals it by
 * sound, by sight and by name in a row — three questions, and then the
 * scorecard, because there is nothing else honest to ask about one bird.
 *
 * A navigation rather than an overlay: this page holds nothing that going back
 * would lose, and it starts straight away (`go=1`) because a setup screen for a
 * three-question round on a bird you just named is all ceremony.
 */
function drillButton(sp: Species, ctx: ViewContext): HTMLElement {
  const btn = el(
    'button.drill.btn.btn-plain',
    {
      type: 'button',
      title: t('species.detail.drill.title', { name: sp.common }),
    },
    icon('binoculars', { size: 17 }),
    t('species.detail.drill.label'),
  ) as HTMLButtonElement;
  btn.addEventListener('click', () => ctx.navigate(`/quiz/field?focus=codes:${sp.code}&go=1`));
  return btn;
}

/** The same toggle the Library rows carry, for the page you land on from one. */
function asideButton(sp: Species): HTMLElement {
  const b = el('button.aside.btn.btn-plain', { type: 'button' }) as HTMLButtonElement;
  const paint = () => {
    const off = isIgnored(sp.code);
    b.classList.toggle('on', off);
    b.replaceChildren(icon(off ? 'undo' : 'set-aside', { size: 20 }));
    b.setAttribute(
      'aria-label',
      off ? t('species.detail.aside.restore') : t('species.detail.aside.setAside'),
    );
    b.setAttribute('aria-pressed', String(off));
    b.title = off
      ? t('species.detail.aside.restore')
      : t('species.detail.aside.setAsideTitle');
  };
  b.addEventListener('click', () => {
    toggleIgnored(sp.code);
    paint();
  });
  paint();
  return b;
}

/**
 * Swipe and arrow-key the same walk the buttons take.
 *
 * Returns the view's teardown, which exists only for the key handler: the touch
 * listeners are on `wrap` and die with it, but a window listener outlives the
 * page it was registered from and would turn pages on the Library underneath.
 */
function walkTheShelf(wrap: HTMLElement, sp: Species, ctx: ViewContext): () => void {
  const go = (dir: -1 | 1) => {
    const { prev, next } = neighbours(sp);
    const to = dir < 0 ? prev : next;
    if (to) ctx.replace(`/species/${to.code}`);
  };

  let x0 = 0;
  let y0 = 0;
  let tracking = false;
  wrap.addEventListener(
    'touchstart',
    (e) => {
      // Two fingers is a pinch on a photograph, and a touch that starts on the
      // sound strip or a thumbnail belongs to that control — a strip is one big
      // play button, and turning the page out from under it would cost a tap
      // the learner meant for the bird.
      const on = (e.target as HTMLElement | null)?.closest('.waveform, .thumbs, button, a');
      tracking = e.touches.length === 1 && !on;
      if (!tracking) return;
      x0 = e.touches[0].clientX;
      y0 = e.touches[0].clientY;
    },
    { passive: true },
  );
  wrap.addEventListener(
    'touchend',
    (e) => {
      if (!tracking) return;
      tracking = false;
      const t = e.changedTouches[0];
      const dx = t.clientX - x0;
      const dy = t.clientY - y0;
      if (Math.abs(dx) < SWIPE_PX || Math.abs(dx) < Math.abs(dy) * SWIPE_BIAS) return;
      // Swiping left drags this page off to the left, bringing the next one in
      // from the right — the direction a book pages, not the way the finger went.
      go(dx < 0 ? 1 : -1);
    },
    { passive: true },
  );

  const onKey = (e: KeyboardEvent) => {
    if (e.metaKey || e.ctrlKey || e.altKey || e.repeat) return;
    const active = document.activeElement as HTMLElement | null;
    if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA')) return;
    if (e.key === 'ArrowLeft') go(-1);
    else if (e.key === 'ArrowRight') go(1);
    else return;
    e.preventDefault();
  };
  window.addEventListener('keydown', onKey);
  return () => window.removeEventListener('keydown', onKey);
}

/** The paragraph this block used to print under itself, now its tooltip. */
const STANDING_WHY = t('species.detail.standingWhy');

/**
 * The three schedules for this bird: how likely you are to recall it right now,
 * and when each skill comes back for it.
 *
 * Filled in after the page paints rather than awaited by the view. The card
 * store is in memory from boot, so in practice this resolves within the same
 * frame — but a species page opened cold from a link would otherwise hold the
 * photos and recordings behind an IndexedDB open, and the media is what the
 * page is for.
 */
function standingBlock(sp: Species): HTMLElement {
  const now = new Date();
  const box = el(
    'section.standing.card',
    { title: STANDING_WHY },
    el('div.label', {}, t('common.yourStanding')),
  );
  void speciesStanding(sp.code, now).then(
    (standing) => {
      box.append(...SKILLS.map((skill) => standingRow(skill, standing.bySkill[skill], now)));
    },
    () => {
      // Progress is unreadable (a broken store, a failed sign-in). The rest of
      // the page is static content and stands on its own, so the block goes
      // rather than sitting there empty under a heading.
      box.remove();
    },
  );
  return box;
}

/** One skill's line: dot, name, retrievability meter, and when it next asks. */
function standingRow(skill: Skill, s: SkillStanding, now: Date): HTMLElement {
  const met = s.standing !== 'unmet';
  const percent = Math.round(s.retrievability * 100);
  return el(
    `div.srow${met ? '' : '.unmet'}`,
    {},
    masteryDot(s.standing, {
      color: SKILL_COLOR[skill],
      label: t('common.skillStanding', {
        skill: SKILL_LABEL[skill],
        standing: standingWord(s.standing),
      }),
    }),
    el('span.who.label', {}, SKILL_LABEL[skill]),
    el('span.pct', {}, met ? t('common.percent', { percent }) : '—'),
    el(
      'span.meter',
      {},
      el('i', {
        style: { width: met ? `${percent}%` : '0%', background: SKILL_COLOR[skill] },
      }),
    ),
    // Every met card has a date, including the "due now" that agrees with its
    // dot. An unmet one has no schedule to report, so it says what it is.
    el('span.when.meta', {}, met ? dueLabel(s.due, now) : standingWord(s.standing)),
  );
}

/**
 * The binomial, with what it means beside it when the pack knows.
 *
 * A parenthetical rather than a line of its own: on this page the name is a
 * caption under the common name, and the gloss is an aside to the caption —
 * "Spatula cyanoptera (spoon · blue-winged)". Nothing here is a question, so
 * unlike the quiz it is shown outright.
 */
function sciLine(sp: Species): HTMLElement {
  const line = el('div.sci', {}, sp.sci);
  if (sp.etym) {
    line.append(
      ' ',
      el(
        'span.gloss',
        { title: t('common.etymTitle', { sci: sp.sci }) },
        t('species.detail.gloss.text', { genus: sp.etym.genus, epithet: sp.etym.epithet }),
      ),
    );
  }
  return line;
}

/**
 * The five axes as pills — size, prey, habitat, feet, nest.
 *
 * Under the photograph rather than under the taxonomy line, which is where it
 * would sit if it were more naming. It is not: it is what the bird does, and the
 * photograph is what the page is opened for, so five rows of pills above it would
 * push the bird under the fold.
 *
 * Nothing at all for a bird nobody has written up, exactly as `etymologyLine` is
 * null for a name nobody has glossed. Absent is a normal state for a hand-written
 * store, and a row of empty labels would present it as missing data. A list rather
 * than a nullable element so it spreads into the page the way `soundGroups` does.
 */
function traitRow(sp: Species, ctx: ViewContext): HTMLElement[] {
  const pills = hasTraits(sp) ? traitPills(sp, ctx) : null;
  return pills ? [pills] : [];
}

/** Hero photo plus, when there is more than one, a strip that swaps it. */
function gallery(sp: Species): HTMLElement {
  const wrap = el('div.gallery', {});
  const hero = speciesPhoto(sp.photos[0], { alt: sp.common });
  const credit = el('div.attrib.note', {}, sp.photos[0] ? photoCredit(sp.photos[0]) : '');
  wrap.append(hero.element);

  if (sp.photos.length > 1) {
    const thumbs = el('div.thumbs', {});
    const buttons = sp.photos.map((photo, i) => {
      const btn = el(
        'button',
        {},
        speciesPhoto(photo, {
          thumb: true,
          alt: t('species.detail.thumbAlt', { name: sp.common, n: i + 1 }),
        }).element,
      ) as HTMLButtonElement;
      if (i === 0) btn.classList.add('on');
      btn.addEventListener('click', () => {
        for (const b of buttons) b.classList.remove('on');
        btn.classList.add('on');
        hero.show(photo);
        credit.textContent = photoCredit(photo);
      });
      thumbs.append(btn);
      return btn;
    });
    wrap.append(thumbs);
  }

  wrap.append(credit);
  if (!sp.photos.length) wrap.append(el('div.empty', {}, t('species.detail.noPhotos')));
  return wrap;
}

/** One block per sound type present, in SOUND_ORDER, each with its players. */
function soundGroups(sp: Species): HTMLElement[] {
  if (!sp.audio.length) return [el('div.empty', {}, t('species.detail.noRecordings'))];

  const blocks: HTMLElement[] = [];
  for (const type of SOUND_ORDER) {
    const group = sp.audio.filter((a) => a.type === type);
    if (!group.length) continue;
    blocks.push(
      el(
        'div.group',
        {},
        el('div.label', {}, t('species.detail.soundGroup', { type, n: group.length })),
        ...group.map(recording),
      ),
    );
  }
  return blocks;
}

function recording(audio: AudioAsset): HTMLElement {
  return el(
    'div.recording',
    {},
    waveformPlayer(audio).element,
    el(
      'div.attrib.note',
      {},
      t('species.credit', {
        author: audio.author,
        license: audio.license,
        source: sourceLabel('xeno-canto'),
      }),
    ),
  );
}
